import { UseQueryOptions } from '@tanstack/react-query';
import { Interval } from '@ui/constants/poll';
import { OpenLimitOrderParams } from '@ui/features/Trade/components/TradeModuleConfirm/hooks/useOpenLimitOrderParams';
import { useDataChainId } from '@ui/hooks/datachain';
import { useQueryOnce } from '@ui/hooks/useQueryOnce';
import { toReadableValue } from '@ui/lib/bignumber';
import { isNil, omit } from 'lodash-es';
import { useDeferredValue, useMemo } from 'react';
import { useAccount, usePublicClient } from 'wagmi';

import { getLimitOrderFacet } from '../../contract-instances/getLimitOrderFacet';

export const useEstimateOpenLimitGasLimit = <TSelect = BigInt>({
  openLimitOrderParams,
  opts,
}: {
  openLimitOrderParams: OpenLimitOrderParams | undefined;
  opts?: Pick<UseQueryOptions<BigInt, Error, TSelect>, 'enabled'>;
}) => {
  const { address } = useAccount();
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });

  const openLimitOrderParamsSnapshot = useDeferredValue(openLimitOrderParams);
  // const payToken = useCollateralToken((s) => s.useCollateralTokenValue());
  // const isNative = payToken?.isNative;

  const LimitOrderFacet = useMemo(() => {
    if (!client) return;

    return getLimitOrderFacet({
      chainId,
      publicClient: client,
    });
  }, [chainId, client]);

  return useQueryOnce({
    meta: {
      type: 'estimateGas',
    },
    queryKey: [
      `LimitOrderFacet.estimate.openLimitOrderETH/openLimitOrder`,
      address,
      chainId,
      client?.uid,
      // openLimitOrderParamsSnapshot,
    ],
    refetchInterval: Interval.FAST,
    queryFn: async () => {
      if (isNil(openLimitOrderParamsSnapshot) || isNil(address))
        return undefined;

      const omitValue = omit(openLimitOrderParamsSnapshot, [
        'value',
        'pythUpdateData',
        'oracleUpdateData',
        'extraFee',
      ]);

      const re = await LimitOrderFacet?.estimateGas.openLimitOrderV2(
        [omitValue, openLimitOrderParamsSnapshot.oracleUpdateData, openLimitOrderParamsSnapshot.extraFee || 0n],
        {
          account: address,
          value: openLimitOrderParamsSnapshot.value,
        },
      );

      if (isNil(re)) return undefined;

      return toReadableValue(re);
    },
    enabled:
      !!address &&
      !!openLimitOrderParamsSnapshot &&
      !!LimitOrderFacet &&
      (opts?.enabled ?? true),
  });
};
